import i18n from "i18next";
import type { FC } from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import type { TerminalEntity } from "src/app/features/Terminal/types/TerminalEntity";
import { useUpdateTableDataMutation } from "../../../../../components";
import { DeleteButton } from "../../../../../components/FeatureButtons/DeleteButton";
import type { ButtonProps } from "../../../../../components/FeatureButtons/types";
import { showSuccessNotification } from "../../../../../components/NotificationAlerts";
import type { AlertActionPayload, ExtendedAlertActionPayload } from "../../../../../components/NotificationAlerts/types";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";

export const TerminalDeleteButton: FC<ButtonProps<TerminalEntity>> = ({ item, items }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const baseURI = `${ResourceName.DATA_GROUPS}/${getAssignedDataGroup()?.code}`;

    const message = t("error.terminal.delete.message", { terminalId: item?.terminalId });

    const contractsMessage = useCallback(() => {
        let contracts = "";
        if (item?.contracts && item?.contracts.length !== 0) {
            const initContracts = `Terminal (${item?.id}) is still connected to these contracts: `;
            contracts = item.contracts?.reduce((prev, curr) => `${prev} ${curr.contractNumber}(${curr.id})`, initContracts);
        }
        return contracts;
    }, [item?.contracts, item?.id]);

    const notifyOkDelete = useCallback(
        (payload: ExtendedAlertActionPayload) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formDeleteSuccess", {
                        id: payload.id,
                        resource: payload.name,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );
    const [deleteRequest] = useUpdateTableDataMutation();

    const deleteCallback = useCallback(() => {
        deleteRequest({
            url: `${baseURI}/${ResourceName.TERMINALS}/${item?.id}`,
            method: "DELETE",
        })
            .unwrap()
            .then((payload) => performDeleteSuccess(payload))
            .catch(() => {
                // error handled in rtkQueryErrorLogger
            });

        const performDeleteSuccess = (payload: AlertActionPayload | undefined) => {
            if (payload) {
                notifyOkDelete(payload);
            }
        };
    }, [baseURI, deleteRequest, notifyOkDelete, item?.id]);

    const modalMessage = `${message}\n\n${contractsMessage()}\n\n`;

    let disabled = false;
    if (items) {
        disabled = items.length !== 1;
    }

    return <DeleteButton isDisabled={disabled} callback={deleteCallback} itemToDelete={item} modalMessage={modalMessage} />;
};
